import { Position } from '../types';
import type { XYPosition } from '../types';
import { DEFAULT_CURVATURE, getControlPoint } from './controlPoint';

export type BezierPathParams = {
  sourceX: number;
  sourceY: number;
  sourcePosition?: Position;
  targetX: number;
  targetY: number;
  targetPosition?: Position;
  curvature?: number;
};

/** `[path, labelX, labelY, offsetX, offsetY]` — the v11 return shape. */
export type BezierPath = [string, number, number, number, number];

/** Weights of a cubic bezier's four points at t = 0.5: (1, 3, 3, 1) / 8. */
const END_WEIGHT = 0.125;
const CONTROL_WEIGHT = 0.375;

const midpoint = (source: XYPosition, sourceControl: XYPosition, targetControl: XYPosition, target: XYPosition) => ({
  x: source.x * END_WEIGHT + sourceControl.x * CONTROL_WEIGHT + targetControl.x * CONTROL_WEIGHT + target.x * END_WEIGHT,
  y: source.y * END_WEIGHT + sourceControl.y * CONTROL_WEIGHT + targetControl.y * CONTROL_WEIGHT + target.y * END_WEIGHT,
});

/** SVG path of the default edge, a cubic bezier leaving and entering through the handles' sides. */
export const getBezierPath = ({
  sourceX,
  sourceY,
  sourcePosition = Position.Bottom,
  targetX,
  targetY,
  targetPosition = Position.Top,
  curvature = DEFAULT_CURVATURE,
}: BezierPathParams): BezierPath => {
  const source = { x: sourceX, y: sourceY };
  const target = { x: targetX, y: targetY };
  const sourceControl = getControlPoint(sourcePosition, source, target, curvature);
  const targetControl = getControlPoint(targetPosition, target, source, curvature);
  const label = midpoint(source, sourceControl, targetControl, target);
  const path = `M${sourceX},${sourceY} C${sourceControl.x},${sourceControl.y} ${targetControl.x},${targetControl.y} ${targetX},${targetY}`;
  return [path, label.x, label.y, Math.abs(label.x - sourceX), Math.abs(label.y - sourceY)];
};
